import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useControllableState } from 'Hooks/useControllableState';
import * as React from 'react';
import { cn } from '~/lib/utils';

function Popover({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const [open, setOpen] = useControllableState({
    value: openProp,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }

    // File drag signals a context switch; close so the popover doesn't block the drop target.
    const handleFileDragStart = () => setOpen(false);

    window.addEventListener('file-drag-start', handleFileDragStart);

    return () => {
      window.removeEventListener('file-drag-start', handleFileDragStart);
    };
  }, [open, setOpen]);

  return (
    <PopoverPrimitive.Root
      data-slot="popover"
      open={open}
      onOpenChange={setOpen}
      {...props}
    />
  );
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  arrowOffset,
  arrowClassName,
  arrowWidth = 10,
  arrowHeight = 6,
  container,
  disablePortal = false,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  arrowOffset?: number;
  arrowClassName?: string;
  arrowWidth?: number;
  arrowHeight?: number;
  container?: HTMLElement | null;
  disablePortal?: boolean;
}) {
  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'bg-popover text-popover-foreground relative data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-[250px] origin-(--radix-popover-content-transform-origin) rounded-lg border p-[6px] shadow-[0px_3px_6px_#00000029] outline-hidden',
        className
      )}
      style={{
        marginLeft:
          arrowOffset !== undefined ? `${arrowOffset}px` : undefined,
      }}
      {...props}
    >
      {props.children}
      <PopoverPrimitive.Arrow
        className={cn(
          '-my-px border-none fill-white popover-arrow-border',
          arrowClassName
        )}
        style={{
          marginLeft:
            arrowOffset !== undefined ? `${arrowOffset}px` : undefined,
        }}
        width={arrowWidth}
        height={arrowHeight}
      />
    </PopoverPrimitive.Content>
  );

  if (disablePortal) {
    return content;
  }

  return (
    <PopoverPrimitive.Portal container={container}>
      {content}
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
